// src/hooks/useStoryboard.ts
import { useState, useEffect, useCallback } from 'react';
import { apiGet, apiPost, apiPut, apiDel } from '../api';

export interface StoryNote {
  entityId: string;
  bookId: string;
  text: string;
  color: string;
  x: number;
  y: number;
  width: number;
  height: number;
  linkedEntityId: string | null;
  linkedEntityIds?: string[];
  /** Stacking order (#1072): where notes overlap, the higher one is in front. Absent counts as 0. */
  z?: number;
  updatedAt: string;
}

export interface StoryFrame {
  id: string;
  label: string;
  color: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface StoryBoard {
  entityId: string;
  bookId: string;
  frames: StoryFrame[];
  updatedAt: string;
}

export interface UseStoryboardResult {
  board: StoryBoard | null;
  notes: StoryNote[];
  loading: boolean;
  error: string | null;
  saveBoard: (frames: StoryFrame[]) => Promise<void>;
  createNote: (note: Omit<StoryNote, 'entityId' | 'updatedAt'>) => Promise<StoryNote>;
  updateNote: (noteId: string, note: Omit<StoryNote, 'entityId' | 'updatedAt'>) => Promise<void>;
  deleteNote: (noteId: string) => Promise<void>;
}

export function useStoryboard(bookId: string | null): UseStoryboardResult {
  const [board, setBoard] = useState<StoryBoard | null>(null);
  const [notes, setNotes] = useState<StoryNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!bookId) return;
    let cancelled = false;
    setBoard(null);
    setNotes([]);
    setLoading(true);
    setError(null);

    Promise.all([
      apiGet(`/storyboard/${bookId}`).then(r => r.body.json() as Promise<StoryBoard | null>),
      apiGet(`/storyboard/${bookId}/notes`).then(r => r.body.json() as unknown as Promise<StoryNote[]>),
    ])
      .then(([b, n]) => {
        if (!cancelled) { setBoard(b); setNotes(n); }
      })
      .catch(() => { if (!cancelled) setError('Failed to load storyboard'); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [bookId]);

  const saveBoard = useCallback(async (frames: StoryFrame[]) => {
    if (!bookId) return;
    await apiPut(`/storyboard/${bookId}`, { frames } as Record<string, unknown>);
    setBoard(prev => prev
      ? { ...prev, frames }
      : { entityId: `STORYBOARD#BOOK#${bookId}`, bookId: `BOOK#${bookId}`, frames, updatedAt: new Date().toISOString() });
  }, [bookId]);

  const createNote = useCallback(async (note: Omit<StoryNote, 'entityId' | 'updatedAt'>): Promise<StoryNote> => {
    if (!bookId) throw new Error('No bookId');
    const res = await apiPost(`/storyboard/${bookId}/notes`, note as Record<string, unknown>);
    const created = await res.body.json() as unknown as StoryNote;
    setNotes(prev => [...prev, created]);
    return created;
  }, [bookId]);

  const updateNote = useCallback(async (noteId: string, note: Omit<StoryNote, 'entityId' | 'updatedAt'>) => {
    if (!bookId) return;
    const encodedNoteId = encodeURIComponent(noteId);
    await apiPut(`/storyboard/${bookId}/notes/${encodedNoteId}`, note as Record<string, unknown>);
    setNotes(prev => prev.map(n => n.entityId === noteId ? { ...n, ...note, updatedAt: new Date().toISOString() } : n));
  }, [bookId]);

  const deleteNote = useCallback(async (noteId: string) => {
    if (!bookId) return;
    const encodedNoteId = encodeURIComponent(noteId);
    const toRestore = notes.find(n => n.entityId === noteId);
    setNotes(prev => prev.filter(n => n.entityId !== noteId));
    try {
      await apiDel(`/storyboard/${bookId}/notes/${encodedNoteId}`);
    } catch {
      if (toRestore) setNotes(prev => [...prev, toRestore]);
    }
  }, [bookId, notes]);

  return { board, notes, loading, error, saveBoard, createNote, updateNote, deleteNote };
}
